import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import {
  ArrowLeft, Bell, Check, CheckCheck, Trash2, Calendar,
  MessageCircle, UserPlus, Clock, AlertCircle, Loader2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notification-center');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notification-center/${notificationId}/read`, {});
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? {...n, read: true} : n)
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notification-center/read-all', {});
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notification-center/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const handleRespondToRequest = async (notification, action) => {
    const requestId = notification.data?.request_id;
    if (!requestId) {
      toast.error('Request data missing');
      return;
    }

    setRespondingTo(requestId);
    try {
      await api.put(`/caregivers/requests/${requestId}/respond`, { action });
      toast.success(action === 'accept' ? 'Request accepted!' : 'Request declined');
      await markAsRead(notification.notification_id);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notification.notification_id
          ? {...n, read: true, _responded: action}
          : n
        )
      );
    } catch (error) {
      const msg = error.response?.data?.detail || `Failed to ${action} request`;
      toast.error(msg);
    } finally {
      setRespondingTo(null);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'time_off_request':
      case 'time_off_response':
        return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'caregiver_request':
      case 'caregiver_response':
        return <UserPlus className="w-5 h-5 text-teal-500" />;
      case 'message':
        return <MessageCircle className="w-5 h-5 text-purple-500" />;
      case 'shift_reminder':
        return <Clock className="w-5 h-5 text-orange-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Just now';
    }
  };

  const isActionableRequest = (notification) => {
    return notification.type === 'caregiver_request'
      && notification.data?.request_id
      && !notification._responded
      && !notification.data?.responded;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Notifications</h1>
              <p className="text-xs sm:text-sm text-slate-500">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead} className="text-xs">
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Notifications</h3>
            <p className="text-slate-500">You're all caught up!</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
            {notifications.map((notification) => (
              <div
                key={notification.notification_id}
                className={`p-4 hover:bg-slate-50 transition-colors ${!notification.read ? 'bg-teal-50/50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm sm:text-base text-slate-900 leading-tight">
                        {notification.title}
                        {!notification.read && (
                          <span className="inline-block w-2 h-2 bg-teal-500 rounded-full ml-2 align-middle" />
                        )}
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => markAsRead(notification.notification_id)}
                            className="h-8 w-8 text-slate-400 hover:text-teal-600"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteNotification(notification.notification_id)}
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-slate-400 mt-1.5">
                      {formatTime(notification.created_at)}
                    </p>

                    {/* Accept/Decline buttons for care requests */}
                    {isActionableRequest(notification) && (
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={() => handleRespondToRequest(notification, 'accept')}
                          disabled={respondingTo === notification.data.request_id}
                          className="bg-teal-500 hover:bg-teal-600 h-8 text-xs"
                        >
                          {respondingTo === notification.data.request_id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Check className="w-3.5 h-3.5" />
                          }
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRespondToRequest(notification, 'decline')}
                          disabled={respondingTo === notification.data.request_id}
                          className="border-red-200 text-red-600 hover:bg-red-50 h-8 text-xs"
                        >
                          <X className="w-3.5 h-3.5" />
                          Decline
                        </Button>
                      </div>
                    )}
                    {(notification._responded || notification.data?.responded) && (
                      <Badge className={`mt-2 ${(notification._responded || notification.data?.responded) === 'accept' ? 'bg-teal-100 text-teal-700' : 'bg-red-100 text-red-700'}`}>
                        {(notification._responded || notification.data?.responded) === 'accept' ? 'Accepted' : 'Declined'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
